import { config } from '@iheartjane/config';
import { Accordion, Box, Flex, useDesktopMediaQuery } from '@iheartjane/reefer';
import type { ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';

import { CheckoutGroupDiscounts } from '@jane/monolith/components/checkout/CheckoutGroupDiscounts';
import {
  CheckoutLocationCallout,
  LogInModal,
  PageLayout,
} from '@jane/shared-ecomm/components';
import {
  useAuthentication,
  useCheckout,
  useGoogleRecaptcha,
} from '@jane/shared-ecomm/providers';
import { LoadingWrapper } from '@jane/shared/components';
import { useShouldShowGold } from '@jane/shared/hooks';
import { useHeadlessApp } from '@jane/shared/providers';
import { postMessageToIframeParent } from '@jane/shared/util';

import { CaliforniaBrochure } from './CaliforniaBrochure/CaliforniaBrochure';
import { ContentContainer, HeaderRow } from './checkout.styles';
import { CheckoutAccordionProvider } from './CheckoutAccordion/CheckoutAccordionProvider';
import { CheckoutAlerts } from './CheckoutAlerts/CheckoutAlerts';
import { CheckoutFormProvider } from './CheckoutFormProvider/CheckoutFormProvider';
import { CheckoutHeader } from './CheckoutHeader/CheckoutHeader';
import { CheckoutInformation } from './CheckoutInformation/CheckoutInformation';
import { CheckoutJaneGoldCallout } from './CheckoutJaneGoldCallout/CheckoutJaneGoldCallout';
import { CheckoutOptions } from './CheckoutOptions/CheckoutOptions';
import { CheckoutPayments } from './CheckoutPayments/CheckoutPayments';
import { CheckoutPaymentsProvider } from './CheckoutPayments/CheckoutPaymentsProvider';
import { CheckoutReview } from './CheckoutReview/CheckoutReview';
import { CheckoutSummary } from './CheckoutSummary/CheckoutSummary';
import { CheckoutTerms } from './CheckoutTerms/CheckoutTerms';
import { CheckoutToggle } from './CheckoutToggle/CheckoutToggle';
import {
  HeadlessKioskProvider,
  useHeadlessKiosk,
} from './HeadlessKioskProvider';
import { useFulfillmentSchedule } from './util/hooks/useFulfillmentSchedule';
import { PaymentMethods } from './util/hooks/usePaymentOptions';
import { useReservationModeLabels } from './util/hooks/useReservationModeLabels';
import { useSelectedFulfillmentTime } from './util/hooks/useSelectedFulfillmentTime';

/**
 * Inner component that renders the location callout.
 * Must be rendered inside CheckoutFormProvider since it uses form-context hooks.
 */
const LocationCallout = () => {
  const { store } = useCheckout();
  const { reservationModeLabel } = useReservationModeLabels(true);
  const { displayName } = useSelectedFulfillmentTime();

  const storeAddress = [
    store.address,
    `${store.city}, ${store.state} ${store.zip}`,
  ]
    .filter(Boolean)
    .join(', ');

  const handleChangeStore = () => {
    postMessageToIframeParent({
      messageType: 'navigationEvent',
      payload: {
        name: 'changeStore',
      },
    });
  };

  return (
    <CheckoutLocationCallout
      fulfillmentTime={displayName}
      onChangeStore={handleChangeStore}
      reservationModeLabel={reservationModeLabel}
      storeAddress={storeAddress}
      storeName={store.name}
    />
  );
};

interface CheckoutContentProps {
  autoScrollMargin: string | undefined;
  bloomUserExperience: boolean | undefined;
  checkoutLocationCalloutEnabled: boolean | undefined;
  children?: ReactNode;
  handleShowLogin: () => void;
  isDesktop: boolean;
}

/**
 * Inner component that renders the main checkout content.
 * Must be rendered inside HeadlessKioskProvider to access kiosk state.
 */
const CheckoutContent = ({
  autoScrollMargin,
  bloomUserExperience,
  isDesktop,
  handleShowLogin,
  checkoutLocationCalloutEnabled,
  children,
}: CheckoutContentProps) => {
  const { isHeadlessKioskMode } = useHeadlessKiosk();

  return (
    <Flex flexDirection="column">
      {!isHeadlessKioskMode &&
        ((bloomUserExperience && isDesktop) || !bloomUserExperience) && (
          <CheckoutToggle handleShowLogin={handleShowLogin} />
        )}

      {checkoutLocationCalloutEnabled && !isHeadlessKioskMode && (
        <LocationCallout />
      )}

      <Accordion autoScrollMargin={autoScrollMargin}>
        {bloomUserExperience && <CheckoutInformation />}

        {!isHeadlessKioskMode && <CheckoutOptions />}

        {!bloomUserExperience && <CheckoutInformation />}

        <CheckoutPayments />
      </Accordion>

      <CheckoutGroupDiscounts />

      {children}
    </Flex>
  );
};

export const Checkout = () => {
  const isDesktop = useDesktopMediaQuery({ size: 'sm', width: 'min' });

  const { ReCAPTCHA } = useGoogleRecaptcha();
  const { isLoadingPickup } = useFulfillmentSchedule();

  const {
    appInfo: {
      disablePrepayment,
      isEmbeddedMode,
      bloomUserExperience,
      headlessLoginUrl,
    },
    cart: { uuid, brand_discounts },
    customer: { authenticated },
    prepaymentProviders,
    guestCheckout: { showLoginModal, setPreAuthSelection, setShowLoginModal },
    store,
  } = useCheckout();

  const { options } = useHeadlessApp();

  const handleClose = () => {
    postMessageToIframeParent({
      messageType: 'analyticsEvent',
      payload: {
        name: 'drawerClosed',
      },
    });
  };

  const showGold = useShouldShowGold(
    store.id ? { store: { id: store.id, state: store.state } } : {}
  );
  const hideJanePayBranding = store
    ? Boolean(store?.jane_pay_integration?.white_label) ||
      Boolean(!store?.jane_pay_integration?.enabled)
    : false;

  const { defaultLogin, register, resetError, resetPassword, ...authProps } =
    useAuthentication();

  const handleShowLogin = () => {
    setShowLoginModal(true);
  };

  // Turn off auto scroll margin if checkout location callout is enabled
  const autoScrollMargin = options?.checkoutLocationCalloutEnabled
    ? undefined
    : isEmbeddedMode
      ? '48px'
      : '140px';
  const hasStrongHold =
    !disablePrepayment &&
    prepaymentProviders?.includes(PaymentMethods.stronghold);
  const hasCanpay =
    !disablePrepayment && prepaymentProviders?.includes(PaymentMethods.canpay);
  const hasSquare =
    !disablePrepayment && prepaymentProviders?.includes(PaymentMethods.square);
  const hasMoneris =
    !disablePrepayment && prepaymentProviders?.includes(PaymentMethods.moneris);

  return (
    <>
      <Helmet>
        {hasStrongHold && (
          <script src="https://cdnjs.cloudflare.com/ajax/libs/jquery/3.4.1/jquery.min.js"></script>
        )}
        {hasStrongHold && (
          <script src="https://api.strongholdpay.com/v2/js"></script>
        )}
        {hasCanpay && <script src={config.canPayV2RemotePaySDK}></script>}
        {hasSquare && <script src={config.squareWebPaymentsSdkUrl}></script>}
        {hasMoneris && (
          <script defer async src={config.monerisJsSdkUrl}></script>
        )}
      </Helmet>
      <HeaderRow>
        <CheckoutHeader
          handleShowLogin={handleShowLogin}
          closeButtonOnCheckout={options?.closeButtonOnCheckout}
          onClose={handleClose}
        />
      </HeaderRow>
      <LoadingWrapper variant="checkout" isLoading={isLoadingPickup}>
        <HeadlessKioskProvider>
          <CheckoutFormProvider>
            <CheckoutPaymentsProvider>
              <CheckoutAccordionProvider>
                <CheckoutAlerts />
                <PageLayout
                  id="content"
                  px={isDesktop ? 64 : 0}
                  py={isDesktop ? (bloomUserExperience ? 40 : 64) : 0}
                >
                  <ContentContainer>
                    <CheckoutContent
                      autoScrollMargin={autoScrollMargin}
                      bloomUserExperience={bloomUserExperience}
                      isDesktop={isDesktop}
                      handleShowLogin={handleShowLogin}
                      checkoutLocationCalloutEnabled={
                        options?.checkoutLocationCalloutEnabled
                      }
                    >
                      {showGold && (
                        <CheckoutJaneGoldCallout
                          cartBrandDiscounts={brand_discounts}
                          headlessLoginUrl={headlessLoginUrl}
                          hideJanePayBranding={hideJanePayBranding}
                        />
                      )}

                      <CheckoutReview />
                    </CheckoutContent>

                    <Box>
                      <CheckoutSummary />

                      <CheckoutTerms />
                    </Box>
                  </ContentContainer>

                  <ReCAPTCHA />

                  <CaliforniaBrochure cartUuid={uuid} state={store?.state} />
                </PageLayout>
              </CheckoutAccordionProvider>
            </CheckoutPaymentsProvider>
          </CheckoutFormProvider>
        </HeadlessKioskProvider>
      </LoadingWrapper>

      {showLoginModal && !authenticated && (
        <LogInModal
          {...authProps}
          onSignInSubmit={defaultLogin}
          onCreateAccountSubmit={register}
          onForgotPasswordSubmit={resetPassword}
          location="checkout"
          open
          onRequestClose={() => {
            resetError();
            setPreAuthSelection(null);
            setShowLoginModal(false);
          }}
        />
      )}
    </>
  );
};
